import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type React from "react";
import { StrictMode } from "react";
import { flushSync } from "react-dom";
import { createRoot, Root } from "react-dom/client";
import "../shared/styles.css";

import { AddressCard } from "./cards/AddressCard";
import { AddressCardDe } from "./cards/AddressCardDe";
import { ApiConfigBar } from "./debug/ApiConfigBar";
import { BerlinMapMount } from "./cards/BerlinMap";
import { DatepickerCard } from "./cards/DatepickerCard";
import { DatepickerCardDe } from "./cards/DatepickerCardDe";
import { LandlordCard } from "./cards/LandlordCard";
import { LandlordCardDe } from "./cards/LandlordCardDe";
import { LooksGoodCard } from "./cards/LooksGoodCard";
import { LooksGoodCardDe } from "./cards/LooksGoodCardDe";
import { MediaBannerCard } from "./cards/MediaBannerCard";
import { SavingsCard } from "./cards/SavingsCard";
import { startHeyflowBridge } from "../shared/heyflow-bridge";

type WidgetName =
  | "address-card"
  | "address-card-de"
  | "landlord-card"
  | "landlord-card-de"
  | "savings-card"
  | "looks-good-card"
  | "looks-good-card-de"
  | "berlin-map"
  | "media-banner"
  | "datepicker-card"
  | "datepicker-card-de"
  | "api-config";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Most widgets are zero-prop components. The BerlinMap widget reads
// configuration from the host element's data-attributes, so it gets a small
// adapter factory that receives the host node.
//
// IMPORTANT: these are React components (capitalised) — we MUST render them
// via JSX (`<Component … />`) so React invokes them inside its own render
// phase. Calling them as plain functions from outside React leaves the hook
// dispatcher null and any hook (e.g. useStorage → useSyncExternalStore)
// throws "Cannot read properties of null".
type WidgetComponent =
  | React.ComponentType<Record<string, never>>
  | React.ComponentType<{ host: HTMLElement }>;

const widgets: Record<WidgetName, WidgetComponent> = {
  "address-card": AddressCard,
  "address-card-de": AddressCardDe,
  "landlord-card": LandlordCard,
  "landlord-card-de": LandlordCardDe,
  "savings-card": SavingsCard,
  "looks-good-card": LooksGoodCard,
  "looks-good-card-de": LooksGoodCardDe,
  "berlin-map": BerlinMapMount,
  "media-banner": MediaBannerCard,
  "datepicker-card": DatepickerCard,
  "datepicker-card-de": DatepickerCardDe,
  "api-config": ApiConfigBar,
};

const roots = new WeakMap<Element, Root>();
// The exact wrapper node React committed into each host. We compare by node
// identity (not by the data-conny-root attribute) so a serialized static copy
// of that node — which carries the same attribute — is never mistaken for the
// live one React owns.
const rootWrappers = new WeakMap<Element, Element>();
let configBarMounted = false;

// Elements managed by ANY copy of this bundle. If the loader <script> ends up
// on the page twice (pasted in Head and again in a screen), both copies share
// this set, so only the first one creates a React root per element.
const globalScope = window as unknown as {
  __connyManagedHosts?: WeakSet<Element>;
};
const managedHosts = (globalScope.__connyManagedHosts ??= new WeakSet());

function mount(name: WidgetName, target: Element): void {
  const Component = widgets[name];
  if (!Component) {
    // eslint-disable-next-line no-console
    console.warn(`[conny] Unknown widget: ${name}`);
    return;
  }
  let root = roots.get(target);

  // Heyflow can detach the DOM React committed while keeping the host element,
  // in two ways during a screen swap:
  //   - it empties the block's children, or
  //   - it re-serializes the block *in place*, replacing React's wrapper with
  //     a static HTML clone (same element, new children).
  // In both cases the root's rendered DOM is gone and re-rendering the same
  // root cannot bring it back — and in the second case React re-commits its
  // live tree while the leftover static clone stays in the host, showing the
  // widget twice. The reliable signal is node identity: a healthy mount keeps
  // the exact wrapper node React created (tracked in rootWrappers) as a live
  // child of the host. If that node is gone or no longer parented by the host,
  // it's wiped — tear down and start fresh (the recreate path below clears any
  // stale children first). A host that was merely *moved* keeps
  // wrapper.parentNode === target, so its root survives.
  const wrapper = rootWrappers.get(target);
  if (root && (!wrapper || wrapper.parentNode !== target)) {
    try {
      root.unmount();
    } catch {
      /* its DOM is already gone */
    }
    roots.delete(target);
    rootWrappers.delete(target);
    managedHosts.delete(target);
    root = undefined;
  }

  let created = false;
  if (!root) {
    // Another bundle instance already owns this element.
    if (managedHosts.has(target)) return;
    // Heyflow re-inserts screens by serialising HTML, so a "new" element can
    // arrive with the previous render baked in as static markup. createRoot
    // does NOT clear existing children — React would append a second card
    // after the stale copy — so clear them first.
    while (target.firstChild) target.removeChild(target.firstChild);
    root = createRoot(target);
    roots.set(target, root);
    managedHosts.add(target);
    created = true;
  }
  const liveRoot = root;
  // Render via JSX so React invokes the component itself. Widgets that opt
  // in to receiving the host element declare a `host` prop.
  const ComponentWithHost = Component as React.ComponentType<{
    host: HTMLElement;
  }>;
  const element =
    target instanceof HTMLElement ? (
      <ComponentWithHost host={target} />
    ) : (
      <ComponentWithHost host={undefined as unknown as HTMLElement} />
    );
  const tree = (
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        {/* Constant wrapper so the wiped-container check above is reliable:
            it exists even while the widget renders null. */}
        <div data-conny-root="">{element}</div>
      </QueryClientProvider>
    </StrictMode>
  );
  if (created) {
    // Commit the first render synchronously so re-scans triggered by
    // unrelated DOM mutations never observe a created-but-not-yet-committed
    // root as "wiped". The flush guarantees the wrapper is in the DOM, so we
    // can record the exact node React owns for the identity check above.
    flushSync(() => liveRoot.render(tree));
    if (target.firstElementChild)
      rootWrappers.set(target, target.firstElementChild);
  } else {
    liveRoot.render(tree);
  }
}

function unmount(target: Element): void {
  const root = roots.get(target);
  if (root) {
    try {
      root.unmount();
    } catch {
      /* its DOM is already gone */
    }
    roots.delete(target);
    rootWrappers.delete(target);
    managedHosts.delete(target);
  }
}

/**
 * The floating "Conny API config" bar is a developer/QA tool. We only mount it
 * when explicitly enabled, so end-users in a live funnel never see it.
 *
 * Enable by ANY of:
 *   - URL contains ?connyDebug=1
 *   - <script src="…conny-widgets.js" data-conny-debug="1"> on the loader
 *   - <meta name="conny-debug" content="1">
 *   - window.CONNY_CONFIG = { debug: true }
 */
function isDebugEnabled(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get("connyDebug") === "1")
      return true;
  } catch {
    /* ignore */
  }
  const tags = document.querySelectorAll<HTMLScriptElement>(
    "script[data-conny-loader], script[src*='conny-widgets']",
  );
  for (const tag of Array.from(tags)) {
    if (tag.getAttribute("data-conny-debug") === "1") return true;
  }
  const meta = document.querySelector<HTMLMetaElement>(
    'meta[name="conny-debug"]',
  );
  if (meta?.content === "1") return true;
  const g = (window as unknown as { CONNY_CONFIG?: { debug?: boolean } })
    .CONNY_CONFIG;
  return Boolean(g?.debug);
}

function mountApiConfigBar(): void {
  if (configBarMounted) return;
  if (!isDebugEnabled()) return;
  configBarMounted = true;
  const host = document.createElement("div");
  host.setAttribute("data-conny-internal", "api-config");
  document.body.appendChild(host);
  mount("api-config", host);
}

function autoMount(root: ParentNode = document): void {
  const els = root.querySelectorAll<HTMLElement>("[data-conny-widget]");
  els.forEach((el) => {
    const name = el.getAttribute("data-conny-widget") as WidgetName | null;
    if (name && widgets[name]) mount(name, el);
  });
}

// When Heyflow removes a screen, unmount the roots of any widget hosts that
// went with it so detached cards stop running queries and storage
// subscriptions. Guard on isConnected: a node that was merely *moved* (removed
// and re-appended in the same batch) must keep its root.
function cleanupRemoved(records: MutationRecord[]): void {
  for (const record of records) {
    for (const node of Array.from(record.removedNodes)) {
      if (!(node instanceof HTMLElement) || node.isConnected) continue;
      const hosts = node.matches("[data-conny-widget]")
        ? [node]
        : Array.from(node.querySelectorAll<HTMLElement>("[data-conny-widget]"));
      hosts.forEach((host) => unmount(host));
    }
  }
}

function init(): void {
  startHeyflowBridge();
  mountApiConfigBar();
  autoMount();

  // Re-scan when Heyflow swaps in new screens.
  const observer = new MutationObserver((records) => {
    cleanupRemoved(records);
    autoMount();
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

declare global {
  interface Window {
    ConnyWidgets: {
      mount: (name: WidgetName, target: Element) => void;
      unmount: (target: Element) => void;
      autoMount: (root?: ParentNode) => void;
    };
  }
}

window.ConnyWidgets = { mount, unmount, autoMount };

export { mount, unmount, autoMount };
